import { useCallback } from "react";

import { DefinedInitialDataOptions, useQuery, UseQueryResult } from "@tanstack/react-query";

import axios from "@/configs/axios";
import { KB_SOURCE, SOURCE_MANUAL, SOURCE_TEAMS, TEAMS_SOURCE } from "@/util/constants";
import { DocumentData } from "@/util/interfaces/DocumentData";

export interface BlobItem {
  name: string;
  metadata: Record<string, string>;
  indexing_status: "IN_PROGRESS" | "SUCCESS" | "NOT_FOUND" | "ERROR";
}

export const baseGetBlobsQueryKey = ["/getBlob"];

export const useGetBlobsQuery = (
  options?: DefinedInitialDataOptions<BlobItem[], Error, DocumentData[], string[]>
): UseQueryResult<DocumentData[], Error> => {
  const getBlobs = useCallback(async () => {
    const url = `${process.env.REACT_APP_SERVER_HOST}/getBlobs/?source=${KB_SOURCE}`;
    const { data } = await axios.get(url);

    return data;
  }, []);

  const queryResult = useQuery<BlobItem[], Error, DocumentData[], string[]>({
    ...options,
    queryFn: getBlobs,
    queryKey: [...baseGetBlobsQueryKey, KB_SOURCE],
    select: (data) => {
      if (data) {
        return data.map((doc: BlobItem, index: number) => {
          const splittedName = doc.name.split(".");
          return {
            url: "",
            metadata: "",
            ...doc.metadata,
            category: doc.metadata.Category,
            documentName: doc.metadata.title,
            file_name: doc.name,
            fileType: splittedName.at(-1) ?? "",
            previewUrl: doc.metadata.preview_url,
            uploaded: doc.metadata.uploaded_date,
            uploadedBy: doc.metadata.uploaded_by,
            path: doc.metadata.path ?? "",
            id: `${index}`,
            folderSource: doc.metadata.teams_source === "true" ? TEAMS_SOURCE : KB_SOURCE,
            source: doc.metadata.teams_source === "true" ? SOURCE_TEAMS : SOURCE_MANUAL,
            indexing_status: doc.indexing_status,
            fileAlias: doc.metadata.file_alias,
            file_extension: splittedName.at(-1) ?? "",
            uuid: doc.metadata.uuid ?? "",
            parentId: null,
          };
        });
      }
      return [];
    },
  });

  return queryResult;
};
